import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Edit2, Mic, Search, Loader2, Play, Clock, X, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDuration(s) {
  if (!s || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function AdminEpisodes() {
  const [episodes, setEpisodes] = useState([]);
  const [shows, setShows] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  // Upload form
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadShowId, setUploadShowId] = useState('');
  const [uploadSeason, setUploadSeason] = useState(1);
  const [uploadEpNum, setUploadEpNum] = useState(1);
  const [uploadFile, setUploadFile] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/episodes?limit=100`),
      axios.get(`${API}/shows`)
    ]).then(([epRes, shRes]) => {
      setEpisodes(epRes.data.episodes || []);
      setShows(shRes.data.shows || []);
    }).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Select an audio file'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadTitle);
    formData.append('description', uploadDescription);
    formData.append('show_id', uploadShowId);
    formData.append('season', uploadSeason);
    formData.append('episode_number', uploadEpNum);
    try {
      await axios.post(`${API}/admin/episodes/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Episode uploaded!');
      setShowUpload(false);
      setUploadTitle(''); setUploadDescription(''); setUploadShowId(''); setUploadSeason(1); setUploadEpNum(1); setUploadFile(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmingDelete(id);
  };

  const executeDelete = async () => {
    if (!confirmingDelete) return;
    try {
      await axios.delete(`${API}/admin/episodes/${confirmingDelete}`);
      toast.success('Episode deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
    setConfirmingDelete(null);
  };

  const handleEdit = async (id, data) => {
    try {
      await axios.put(`${API}/admin/episodes/${id}`, data);
      toast.success('Episode updated');
      setEditing(null);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const getShowName = (id) => shows.find(s => s.id === id)?.name || '—';

  return (
    <div data-testid="admin-episodes">
      {confirmingDelete && <DeleteConfirmModal message="Delete this episode? This cannot be undone." onConfirm={executeDelete} onCancel={() => setConfirmingDelete(null)} />}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Recorded Episodes</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{episodes.length} episodes</p>
        </div>
        <button
          data-testid="upload-episode-button"
          onClick={() => setShowUpload(!showUpload)}
          className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-6 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
        >
          <Upload size={18} /> Upload Episode
        </button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <form data-testid="episode-upload-form" onSubmit={handleUpload} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 mb-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Title *</label>
              <input data-testid="episode-title-input" type="text" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Episode title" className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full placeholder:text-slate-600" required />
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Show</label>
              <select data-testid="episode-show-select" value={uploadShowId} onChange={e => setUploadShowId(e.target.value)} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full">
                <option value="">Select show...</option>
                {shows.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Season</label>
                <input data-testid="episode-season-input" type="number" min="1" value={uploadSeason} onChange={e => setUploadSeason(parseInt(e.target.value) || 1)} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full" />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Episode #</label>
                <input data-testid="episode-number-input" type="number" min="1" value={uploadEpNum} onChange={e => setUploadEpNum(parseInt(e.target.value) || 1)} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Description</label>
              <textarea data-testid="episode-description-input" value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} placeholder="Episode description" rows={2} className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] outline-none w-full placeholder:text-slate-600 resize-none" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Audio File *</label>
            <div onClick={() => fileRef.current?.click()} className={`bg-[#0A0C10] border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${uploadFile ? 'border-[#D4AF37]/50' : 'border-white/10 hover:border-[#D4AF37]/50'}`}>
              <Mic size={24} className="mx-auto text-[#94A3B8]/50 mb-2" />
              <p className="text-[#94A3B8] text-sm">{uploadFile ? uploadFile.name : 'Click to select audio file'}</p>
              {uploadFile && <p className="text-[#D4AF37] text-xs mt-1">{(uploadFile.size / (1024 * 1024)).toFixed(1)} MB</p>}
            </div>
            <input ref={fileRef} data-testid="episode-file-input" type="file" accept="audio/*" onChange={e => setUploadFile(e.target.files[0])} className="hidden" />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowUpload(false)} className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 rounded-full px-6 py-2.5">Cancel</button>
            <button data-testid="episode-upload-submit" type="submit" disabled={uploading} className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 flex items-center gap-2 disabled:opacity-50">
              {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> Upload</>}
            </button>
          </div>
        </form>
      )}

      {/* Episodes List */}
      <div className="space-y-2">
        {episodes.length === 0 ? (
          <div className="text-center py-16 bg-[#171A21] rounded-2xl border border-white/5">
            <Mic size={32} className="mx-auto text-[#94A3B8]/40 mb-2" />
            <p className="text-[#94A3B8]">No episodes yet. Upload your first recorded show!</p>
          </div>
        ) : (
          episodes.map((ep, i) => (
            <EpisodeRow
              key={ep.id}
              episode={ep}
              index={i}
              editing={editing === ep.id}
              shows={shows}
              onEdit={() => setEditing(ep.id)}
              onCancelEdit={() => setEditing(null)}
              onSaveEdit={(data) => handleEdit(ep.id, data)}
              onDelete={() => handleDelete(ep.id)}
              getShowName={getShowName}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EpisodeRow({ episode, index, editing, shows, onEdit, onCancelEdit, onSaveEdit, onDelete, getShowName }) {
  const [title, setTitle] = useState(episode.title);
  const [description, setDescription] = useState(episode.description || '');

  if (editing) {
    return (
      <div className="bg-[#171A21] rounded-xl border border-white/5 p-4 space-y-3">
        <input value={title} onChange={e => setTitle(e.target.value)} className="bg-[#0A0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm w-full" placeholder="Title" />
        <textarea value={description} onChange={e => setDescription(e.target.value)} className="bg-[#0A0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm w-full resize-none" rows={2} placeholder="Description" />
        <div className="flex justify-end gap-2">
          <button onClick={() => onSaveEdit({ title, description })} className="p-2 rounded-lg hover:bg-emerald-500/20 text-emerald-400"><Check size={16} /></button>
          <button onClick={onCancelEdit} className="p-2 rounded-lg hover:bg-white/10 text-[#94A3B8]"><X size={16} /></button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`admin-episode-${index}`} className="bg-[#171A21] rounded-xl border border-white/5 p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
      <div className="w-11 h-11 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center shrink-0">
        <Mic size={18} className="text-[#D4AF37]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm truncate">{episode.title}</p>
        <div className="flex items-center gap-2 text-xs text-[#94A3B8] mt-0.5">
          <span className="text-[#D4AF37]">{getShowName(episode.show_id)}</span>
          <span>S{episode.season} E{episode.episode_number}</span>
          {episode.duration > 0 && <span className="flex items-center gap-0.5"><Clock size={10} /> {formatDuration(episode.duration)}</span>}
          <span>{episode.play_count} plays</span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button data-testid={`edit-episode-${index}`} onClick={onEdit} className="p-2 rounded-lg hover:bg-white/10 text-[#94A3B8] hover:text-white"><Edit2 size={14} /></button>
        <button data-testid={`delete-episode-${index}`} onClick={onDelete} className="p-2 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
